import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Table, Thead, Tbody, Tr, Th, Td, Divider
} from "@chakra-ui/react";
import { FaTint, FaMapMarkerAlt, FaArrowRight, FaExclamationTriangle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterLocationGoldCoast: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/water-quality/gold-coast"
        fallbackTitle="Gold Coast Water Quality — One Water Australia"
        fallbackDescription="Gold Coast water quality: what the Seqwater data shows, what it doesn't, and what Gold Coast homeowners can do about it."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 14, md: 20 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <HStack>
              <Icon as={FaMapMarkerAlt} color={WATER_BLUE} />
              <Text fontSize="sm" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider">
                Location Guide
              </Text>
            </HStack>
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Gold Coast Water Quality
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
              What the data shows — and what it doesn't.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Gold Coast Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Featured image */}
      <Box h={{ base: "240px", md: "400px" }} overflow="hidden">
        <Box
          as="img"
          src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80"
          alt="Gold Coast aerial view — water quality guide for Gold Coast homes"
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12} align="stretch">

          {/* Where it comes from */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Where Gold Coast Water Comes From</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Gold Coast water is supplied by Seqwater, Queensland's bulk water utility, through a network drawing from several sources. Understanding these sources is important because the origin of your water affects its specific characteristics.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              {[
                {
                  source: "Hinze Dam (Advancetown Lake)",
                  detail: "The primary storage reservoir for the Gold Coast, located in the Nerang River catchment in the Hinterland. Fresh, surface-collected water treated at Molendinar Water Treatment Plant.",
                },
                {
                  source: "Gold Coast Desalination Plant, Tugun",
                  detail: "One of Australia's major desalination facilities. Provides a drought-resilient supply by treating ocean water. Desalination-sourced water has different mineral characteristics from dam water — typically lower hardness, lower TDS.",
                },
                {
                  source: "Seqwater regional grid",
                  detail: "Gold Coast is connected to the wider SE QLD water grid, allowing water from Wivenhoe, Somerset, and other catchments to supplement supply during high demand or low-storage periods.",
                },
                {
                  source: "Molendinar Water Treatment Plant",
                  detail: "The primary treatment facility for Gold Coast water. Treats raw water from Hinze Dam before it enters the distribution network serving most Gold Coast suburbs.",
                },
              ].map((item) => (
                <Box key={item.source} p={5} bg={ICE_MIST} borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.source}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What the data shows */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What the Data Shows</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Based on Seqwater's published water quality data for the Gold Coast supply zone, here are the key parameters for treated water entering the distribution network:
            </Text>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg={ICE_MIST}>
                  <Tr>
                    <Th color={NAVY} py={4}>Parameter</Th>
                    <Th color={NAVY} py={4}>Typical Range (Gold Coast)</Th>
                    <Th color={NAVY} py={4}>ADWG Guideline</Th>
                    <Th color={NAVY} py={4}>Practical significance</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[
                    { param: "Chlorine (free residual)", range: "0.2–0.8 mg/L", guideline: "≤5 mg/L", sig: "Present and detectable in taste for some people." },
                    { param: "Fluoride", range: "0.6–0.9 mg/L", guideline: "≤1.5 mg/L", sig: "Within guidelines. Added for dental health." },
                    { param: "Turbidity", range: "<0.5 NTU", guideline: "≤5 NTU", sig: "Consistently clear. Well within guidelines." },
                    { param: "pH", range: "7.2–7.8", guideline: "6.5–8.5", sig: "Neutral. No issues." },
                    { param: "Hardness (CaCO₃)", range: "50–130 mg/L (varies by zone)", guideline: "No health guideline", sig: "Moderate. Growth corridors (Coomera, Pimpama) may vary from older suburbs." },
                    { param: "Total Dissolved Solids", range: "150–280 mg/L", guideline: "≤600 mg/L (aesthetic)", sig: "Within aesthetic guidelines. Desalination blending affects this figure." },
                  ].map((row) => (
                    <Tr key={row.param} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="600" color={NAVY} fontSize="sm">{row.param}</Td>
                      <Td fontSize="sm" color={DEEP_AQUA} fontWeight="500">{row.range}</Td>
                      <Td fontSize="sm" color="gray.600">{row.guideline}</Td>
                      <Td fontSize="sm" color="gray.600">{row.sig}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Text fontSize="xs" color="gray.500" mt={3}>
              Data based on Seqwater Annual Drinking Water Quality Report 2025–26. Figures represent typical ranges — individual zone variation applies. Verify against latest published report for your specific supply zone.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* The last mile — Gold Coast specific */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>The Last Mile — Gold Coast Specific</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Seqwater's data tells you about the water leaving the treatment plant. What reaches your tap is also affected by your local infrastructure and your suburb's history. This varies significantly across the Gold Coast.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              {[
                {
                  area: "Growth corridors (Coomera, Pimpama, Hope Island)",
                  detail: "Newer infrastructure with modern PVC service connections. Generally lower risk of pipe-introduced contaminants. However, rapidly expanding supply areas can experience more variable pressure and flow conditions.",
                },
                {
                  area: "Established suburbs (Southport, Burleigh, Surfers Paradise)",
                  detail: "Older distribution infrastructure. Some service connections and internal building plumbing from the 1960s–1980s may contain galvanised steel or copper with older solder joins. Distance from Molendinar treatment plant increases transit time.",
                },
                {
                  area: "Hinterland and rural fringe (Tamborine, Canungra)",
                  detail: "Some properties in the hinterland are on tank or bore water rather than reticulated supply. Different treatment requirements apply entirely.",
                },
                {
                  area: "Units and older apartment buildings",
                  detail: "Shared service connections and internal plumbing of older multi-residential buildings can introduce variables not captured in Seqwater supply data. Hot water systems, roof tanks, and common plumbing are building responsibility.",
                },
              ].map((item) => (
                <Box key={item.area} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.area}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Desalination note */}
          <Box p={6} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`}>
            <HStack spacing={2} mb={3}>
              <Icon as={FaExclamationTriangle} color={DEEP_AQUA} />
              <Text fontWeight="700" color={NAVY} fontSize="sm">Note on desalination blending</Text>
            </HStack>
            <Text fontSize="sm" color="gray.700" lineHeight="tall">
              The Gold Coast Desalination Plant at Tugun produces water with different mineral characteristics from dam-sourced water — typically lower hardness and different TDS. When desalination output is blended with Hinze Dam water in the supply network, the ratio affects the water quality characteristics reaching your property. During high-demand or drought periods, desalination contribution increases. This seasonal variation in water characteristics is not always apparent to homeowners.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What Gold Coast homeowners can do */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What Gold Coast Homeowners Can Do</Heading>
            <VStack spacing={4} align="stretch">
              {[
                {
                  action: "Check the latest Seqwater supply zone data",
                  detail: "Seqwater publishes annual water quality data by supply zone. This gives you the best available picture of treated water quality for your area.",
                },
                {
                  action: "Book a free water assessment with One Water Australia",
                  detail: "We assess your specific supply, identify local infrastructure factors relevant to your property, and recommend — or don't recommend — a filtration solution.",
                },
                {
                  action: "UNSW lab test for your specific tap",
                  detail: "Our UNSW partner laboratory can test water from your specific kitchen tap — capturing anything introduced between Seqwater's monitoring points and your glass.",
                  highlight: true,
                },
              ].map((item) => (
                <Box key={item.action} p={5} bg={item.highlight ? ICE_MIST : "white"} border="1px solid" borderColor={item.highlight ? `${WATER_BLUE}40` : "gray.200"} borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.action}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          {/* CTA */}
          <Box bg={NAVY} p={10} borderRadius="2xl" textAlign="center">
            <VStack spacing={5}>
              <Icon as={FaMapMarkerAlt} boxSize={8} color={WATER_BLUE} />
              <Heading size="lg" color="white">Gold Coast Water Assessment</Heading>
              <Text color="whiteAlpha.800" maxW="lg">
                We understand Gold Coast water specifically — the supply network, the infrastructure variation by suburb, and the Seqwater data for your zone. Book a free assessment and know exactly what's in your water.
              </Text>
              <Button
                as={RouterLink}
                to="/contact"
                bg={WATER_BLUE}
                color={NAVY}
                size="lg"
                fontWeight="bold"
                _hover={{ bg: "#8DC5E0" }}
                transition="all 0.2s"
              >
                Book Free Water Assessment
              </Button>
            </VStack>
          </Box>

          {/* Related */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Related Reading</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              {[
                { title: "Seqwater 2026 Analysis", href: "/water/blog/seqwater-annual-report-2026" },
                { title: "Is Tap Water Safe?", href: "/water/blog/tap-water-safe-australia" },
                { title: "Hard Water Australia", href: "/water/blog/hard-water-australia" },
                { title: "Chlorine in Tap Water", href: "/water/blog/chlorine-in-tap-water" },
                { title: "Under-Sink Water Filter", href: "/water/water-filters/under-sink-system" },
              ].map((l) => (
                <Box
                  key={l.title}
                  as={RouterLink}
                  to={l.href}
                  p={3}
                  bg="gray.50"
                  borderRadius="lg"
                  display="block"
                  textDecoration="none"
                  _hover={{ bg: ICE_MIST }}
                  transition="all 0.2s"
                >
                  <Text fontSize="sm" color={NAVY} fontWeight="500">{l.title}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterLocationGoldCoast;
